import { Vector3 } from "three";

export interface FigureData {
  vertices: [number, number, number][];
  edges: [number, number][];
  /**
   * 各頂点が属する 3 面それぞれの単位法線:
   *   [0]: 立方体の角に対応する三角形面の法線
   *   [1], [2]: 切頂軸以外の 2 軸方向の正八角形面の法線
   *
   * 凸多面体の頂点は、属する面のうち少なくとも 1 面がカメラ側を向けば視認できる。
   * これを「3 面 OR 判定」で正確かつ数値誤差ゼロにオクルージョン判定できる。
   */
  vertexFaceNormals: [
    [number, number, number],
    [number, number, number],
    [number, number, number],
  ][];
}

// 八角形の切り口パラメータ XI。立方体面 [-1,1] で、八角形の直線辺の端点が
// (±XI, 1) に来る。値で八角形の形が変わる:
//   yaesu  : 0.464           … YAESU CoRE ブランドロゴ (Asset 1.svg)
//                              体対角線投影で内三角形 : 外十二角形 = 0.30 : 1、
//                              中三角形 : 外 = 0.82 : 1 になる XI = 1.733/3.733
//   regular: √2-1 ≈ 0.4142 … 正八角形
//   square : 0.6495          … 面取り小・四角寄り
//   diamond: 0.2469          … 面取り大・菱形寄り
//   sharp  : 0.15            … 面取り最大・鋭角な菱形寄り
// いずれも頂点数 24 / 辺 36 でトポロジは同一、位置 (XI) だけが変わる。
export const OCTAGON_XI = {
  yaesu: 0.464,
  regular: Math.SQRT2 - 1,
  square: 0.6495,
  diamond: 0.2469,
  sharp: 0.15,
} as const;

export type OctagonPreset = keyof typeof OCTAGON_XI;

const FIGURE_SCALE = 0.82;

function generateVertices3D(xi: number): Vector3[] {
  const verts: Vector3[] = [];
  for (const sx of [-1, 1])
    for (const sy of [-1, 1])
      for (const sz of [-1, 1])
        verts.push(new Vector3(sx * xi, sy, sz));
  for (const sx of [-1, 1])
    for (const sy of [-1, 1])
      for (const sz of [-1, 1])
        verts.push(new Vector3(sx, sy * xi, sz));
  for (const sx of [-1, 1])
    for (const sy of [-1, 1])
      for (const sz of [-1, 1])
        verts.push(new Vector3(sx, sy, sz * xi));
  return verts;
}

function generateEdges(verts: Vector3[], xi: number): [number, number][] {
  // 切頂立方体の辺は 2 種類:
  //   - 八角形の直線辺: 長さ 2·XI ((XI,1,1)-(-XI,1,1) 等、隣接する八角形と共有)
  //   - コーナー三角形の面取り辺: 長さ (1-XI)·√2 ((XI,1,1)-(1,XI,1) 等)
  // XI = √2-1 (正八角形) のときは両者が一致するが、比率を変えると別の長さに
  // なるため、両方を検出する。合計 36 辺 (直線 12 + 面取り 24)。
  const straightLen = 2 * xi;
  const chamferLen = (1 - xi) * Math.SQRT2;
  const eps = 1e-3;
  const edges: [number, number][] = [];
  for (let i = 0; i < verts.length; i++) {
    for (let j = i + 1; j < verts.length; j++) {
      const d = verts[i].distanceTo(verts[j]);
      if (Math.abs(d - straightLen) < eps || Math.abs(d - chamferLen) < eps) {
        edges.push([i, j]);
      }
    }
  }
  return edges;
}

function computeFaceNormals(v: Vector3): [Vector3, Vector3, Vector3] {
  const ax = Math.abs(v.x);
  const ay = Math.abs(v.y);
  const az = Math.abs(v.z);
  const sx = Math.sign(v.x);
  const sy = Math.sign(v.y);
  const sz = Math.sign(v.z);

  const xCut = ax < ay && ax < az;
  const yCut = !xCut && ay < ax && ay < az;

  const tri = new Vector3(sx, sy, sz).normalize();
  let oct1: Vector3;
  let oct2: Vector3;
  if (xCut) {
    oct1 = new Vector3(0, sy, 0);
    oct2 = new Vector3(0, 0, sz);
  } else if (yCut) {
    oct1 = new Vector3(sx, 0, 0);
    oct2 = new Vector3(0, 0, sz);
  } else {
    oct1 = new Vector3(sx, 0, 0);
    oct2 = new Vector3(0, sy, 0);
  }
  return [tri, oct1, oct2];
}

export function buildOctagonOnCube(
  xi: number = OCTAGON_XI.yaesu,
): FigureData {
  const verts3D = generateVertices3D(xi);
  const edges = generateEdges(verts3D, xi);
  const faceNormals = verts3D.map(computeFaceNormals);

  return {
    vertices: verts3D.map((v): [number, number, number] => [
      v.x * FIGURE_SCALE,
      v.y * FIGURE_SCALE,
      v.z * FIGURE_SCALE,
    ]),
    edges,
    vertexFaceNormals: faceNormals.map(
      (faces): [
        [number, number, number],
        [number, number, number],
        [number, number, number],
      ] => [
        [faces[0].x, faces[0].y, faces[0].z],
        [faces[1].x, faces[1].y, faces[1].z],
        [faces[2].x, faces[2].y, faces[2].z],
      ],
    ),
  };
}
